import { useState, useRef, useCallback, useEffect } from "react";
import { Tldraw, Editor } from "tldraw";
import "tldraw/tldraw.css";
import { fetchNotesWithCanvas, exportAllPdfs, NoteWithCanvas } from "./api";
import "./ExportPage.css";

interface ExportPageProps {
  onBack: () => void;
}

type ExportPhase = "loading" | "rendering" | "creating-zip" | "complete" | "error";

interface ExportProgress {
  phase: ExportPhase;
  current: number;
  total: number;
  currentNoteName: string;
  errorMessage?: string;
}

export default function ExportPage({ onBack }: ExportPageProps) {
  const [progress, setProgress] = useState<ExportProgress>({
    phase: "loading",
    current: 0,
    total: 0,
    currentNoteName: "",
  });
  const [notes, setNotes] = useState<NoteWithCanvas[]>([]);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(-1);
  const [isExporting, setIsExporting] = useState(false);
  const editorRef = useRef<Editor | null>(null);
  const collectedSvgs = useRef<Array<{
    name: string;
    folderName: string;
    svg: string;
    width: number;
    height: number;
  }>>([]);
  const cancelledRef = useRef(false);

  // Start export process on mount
  useEffect(() => {
    startExport();
  }, []);

  const startExport = async () => {
    setIsExporting(true);
    cancelledRef.current = false;
    collectedSvgs.current = [];

    try {
      // Fetch all notes with canvas data
      setProgress({ phase: "loading", current: 0, total: 0, currentNoteName: "Fetching notes..." });
      const fetchedNotes = await fetchNotesWithCanvas();

      // Filter notes that have canvas data with shapes
      const notesWithContent = fetchedNotes.filter((note) => {
        if (!note.canvasData) return false;
        try {
          const data = JSON.parse(note.canvasData);
          // The sync library stores data as:
          // { documents: [{ state: {...}, lastChangedClock: N }, ...], schema: {...}, ... }
          // Each document in the array has a state with keys like "shape:xxx", "page:xxx", etc.
          if (data.documents && Array.isArray(data.documents)) {
            return data.documents.some((doc: any) => {
              // The state IS the record itself, with id like "shape:xxx"
              const state = doc.state;
              if (state && typeof state === 'object' && state.id) {
                return String(state.id).startsWith("shape:");
              }
              return false;
            });
          }
          return false;
        } catch {
          return false;
        }
      });

      if (notesWithContent.length === 0) {
        setProgress({
          phase: "error",
          current: 0,
          total: 0,
          currentNoteName: "",
          errorMessage: "No notes with content to export",
        });
        return;
      }

      setNotes(notesWithContent);
      setProgress({
        phase: "rendering",
        current: 0,
        total: notesWithContent.length,
        currentNoteName: notesWithContent[0]?.name || "",
      });

      // Start processing first note
      setCurrentNoteIndex(0);
    } catch (err) {
      setProgress({
        phase: "error",
        current: 0,
        total: 0,
        currentNoteName: "",
        errorMessage: err instanceof Error ? err.message : "Failed to start export",
      });
    }
  };

  const [editorReady, setEditorReady] = useState(false);

  const handleEditorMount = useCallback((editor: Editor) => {
    editorRef.current = editor;
    setEditorReady(true);
  }, [currentNoteIndex]);

  // Process note when editor mounts or currentNoteIndex changes
  useEffect(() => {
    if (currentNoteIndex < 0 || currentNoteIndex >= notes.length) return;
    if (!editorReady || !editorRef.current) return;
    if (cancelledRef.current) return;

    const processCurrentNote = async () => {
      const note = notes[currentNoteIndex];
      const editor = editorRef.current;

      if (!editor) {
        moveToNextNote();
        return;
      }

      if (!note.canvasData) {
        moveToNextNote();
        return;
      }

      try {
        // Load the snapshot into the editor
        const snapshotData = JSON.parse(note.canvasData);

        // Convert sync snapshot to store snapshot format
        let storeData: Record<string, any> = {};
        if (snapshotData.documents && Array.isArray(snapshotData.documents)) {
          for (const doc of snapshotData.documents) {
            if (doc.state && typeof doc.state === 'object' && doc.state.id) {
              storeData[doc.state.id] = doc.state;
            }
          }
        }

        const storeSnapshot = {
          store: storeData,
          schema: snapshotData.schema,
        };

        editor.store.loadStoreSnapshot(storeSnapshot);

        // Wait for rendering
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Get all shape IDs
        const shapeIds = [...editor.getCurrentPageShapeIds()];

        if (shapeIds.length > 0) {
          const result = await editor.getSvgString(shapeIds);
          if (result) {
            collectedSvgs.current.push({
              name: note.name,
              folderName: note.folderName,
              svg: result.svg,
              width: result.width,
              height: result.height,
            });
          }
        }

        moveToNextNote();
      } catch {
        moveToNextNote();
      }
    };

    const moveToNextNote = () => {
      // Update progress
      setProgress((prev) => ({
        ...prev,
        current: currentNoteIndex + 1,
        currentNoteName: notes[currentNoteIndex + 1]?.name || "",
      }));

      // Reset editor ready for next note
      setEditorReady(false);

      // Move to next note or finish
      if (currentNoteIndex + 1 < notes.length) {
        setCurrentNoteIndex(currentNoteIndex + 1);
      } else {
        // All notes processed, create ZIP
        createZip();
      }
    };

    // Small delay to ensure editor is ready
    const timer = setTimeout(processCurrentNote, 200);
    return () => clearTimeout(timer);
  }, [currentNoteIndex, notes, editorReady]);

  const createZip = async () => {
    if (cancelledRef.current) return;

    if (collectedSvgs.current.length === 0) {
      setProgress({
        phase: "error",
        current: 0,
        total: 0,
        currentNoteName: "",
        errorMessage: "No notes could be exported",
      });
      return;
    }

    setProgress((prev) => ({
      ...prev,
      phase: "creating-zip",
      currentNoteName: "Creating ZIP file...",
    }));

    try {
      const blob = await exportAllPdfs(collectedSvgs.current);

      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().split("T")[0];
      a.download = `whiteboard-export-${date}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      setProgress((prev) => ({
        ...prev,
        phase: "complete",
        currentNoteName: "",
      }));
    } catch (err) {
      setProgress({
        phase: "error",
        current: 0,
        total: 0,
        currentNoteName: "",
        errorMessage: err instanceof Error ? err.message : "Failed to create ZIP",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleCancel = () => {
    cancelledRef.current = true;
    setIsExporting(false);
    onBack();
  };

  const getProgressPercent = () => {
    if (progress.total === 0) return 0;
    return Math.round((progress.current / progress.total) * 100);
  };

  return (
    <div className="export-page">
      <div className="export-container">
        <div className="export-header">
          <h1>Export All Notes</h1>
        </div>

        <div className="export-content">
          {progress.phase === "loading" && (
            <div className="export-status">
              <div className="export-spinner" />
              <p>Loading notes...</p>
            </div>
          )}

          {progress.phase === "rendering" && (
            <div className="export-status">
              <div className="export-progress-bar">
                <div
                  className="export-progress-fill"
                  style={{ width: `${getProgressPercent()}%` }}
                />
              </div>
              <p className="export-progress-text">
                Rendering note {progress.current + 1} of {progress.total}
              </p>
              <p className="export-current-note">{progress.currentNoteName}</p>
              <button className="export-cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          )}

          {progress.phase === "creating-zip" && (
            <div className="export-status">
              <div className="export-spinner" />
              <p>Creating ZIP file...</p>
              <p className="export-note-count">
                {collectedSvgs.current.length} notes processed
              </p>
            </div>
          )}

          {progress.phase === "complete" && (
            <div className="export-status export-complete">
              <div className="export-success-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <p>Export complete!</p>
              <p className="export-note-count">
                {collectedSvgs.current.length} notes exported
              </p>
              <button className="export-back-btn" onClick={onBack}>
                Back to Profile
              </button>
            </div>
          )}

          {progress.phase === "error" && (
            <div className="export-status export-error">
              <div className="export-error-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>
              <p>Export failed</p>
              <p className="export-error-message">{progress.errorMessage}</p>
              <div className="export-error-buttons">
                <button className="export-retry-btn" onClick={startExport}>
                  Try Again
                </button>
                <button className="export-back-btn" onClick={onBack}>
                  Back to Profile
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Hidden tldraw editor for rendering */}
        {isExporting && currentNoteIndex >= 0 && notes[currentNoteIndex] && (
          <div className="export-hidden-editor">
            <Tldraw
              key={`export-${currentNoteIndex}`}
              onMount={handleEditorMount}
              autoFocus={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}
